// src/components/lead/sections/LeadsModePanel.tsx
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import type { Lead } from "@/types/lead";
import { type ReactNode } from "react";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { LeadsModeRecentLeadsBlock } from "./LeadsModeRecentLeadsBlock";
import { LeadsModeTranscriptBlock } from "./LeadsModeTranscriptBlock";
import type { LeadTransferCandidate } from "@/lib/lead/transferLeadOwner";

// `scopedOrganizationId` + the ads/sales-console nav callbacks + the
// follow-up attention count used to live on this prop type so the
// in-rail `SalesDailyChecklistWidget` could surface its row CTAs and
// follow-up alert. Slice C of
// docs/planning/activity_dock_workflow_facilitation.md §6 moved the
// widget to the `ActivityFloatingDock` (where the shell wires those
// callbacks via `useLeadWorkspaceShellDockHandlers`), so this panel
// no longer needs them.
export function LeadsModePanel(props: {
  // top-level actions
  startNewLead: () => void;
  isListening: boolean;
  isAsrReconnecting?: boolean;
  asrStatus?: string;
  stopListening: () => void;

  isStoppingUi: boolean;

  // current lead + transcript
  currentLead: Lead | null;
  transcript: string;
  showTranscriptDetails: boolean;
  setShowTranscriptDetails: (fn: (v: boolean) => boolean) => void;

  continueListening: () => void;
  closeCurrentLead: () => void;

  // lead view shell (FullLeadView/CompactLeadView already computed)
  leadView: ReactNode;

  // LeadSummary block (caller passes the component node to avoid prop explosion)
  leadSummaryNode: ReactNode;

  // Recent leads
  visibleRecentLeads: Lead[];
  filteredRecentLeadsCount: number;
  hasMoreLeads: boolean;
  showAllRecentLeads: boolean;
  setShowAllRecentLeads: (fn: (v: boolean) => boolean) => void;

  recentFilter: "all" | "today" | "unsent";
  setRecentFilter: (v: "all" | "today" | "unsent") => void;
  recentSort: "recent" | "oldest" | "status";
  setRecentSort: (v: "recent" | "oldest" | "status") => void;

  showLeadsManagerFilters?: boolean;
  showLeadsOwnerFilter?: boolean;
  recentLocationFilter?: string | "all";
  setRecentLocationFilter?: (v: string | "all") => void;
  recentOwnerFilter?: string | "all";
  setRecentOwnerFilter?: (v: string | "all") => void;
  locationOptions?: Array<{ id: string; label: string }>;
  ownerCandidates?: LeadTransferCandidate[];
  ownerLabelByUserId?: Map<string, string>;
  locationNameById?: Map<string, string>;

  // helpers/actions
  openLeadInEditor: (lead: Lead) => void;
  deleteLead: (id: string) => void;
  mergeLeadIntoCurrent: (sourceId: string) => void;
  isMergeCandidate: (a: Lead, b: Lead) => boolean;

  formatLeadAge: (ageMs: number) => string;

  // used for disabling merge button
  currentLeadId: string | null;

  commitTranscriptToCurrentLead: (next: string) => void;

  // DEV TOOLS
  devMode: boolean;
  devToolsNode?: ReactNode;
}) {
  const { appDisplayName } = useOrgSettings();

  const {
    startNewLead,
    isListening,
    isAsrReconnecting,
    asrStatus,
    stopListening,
    isStoppingUi,

    currentLead,
    transcript,
    showTranscriptDetails,
    setShowTranscriptDetails,

    continueListening,
    closeCurrentLead,

    leadView,
    leadSummaryNode,

    visibleRecentLeads,
    filteredRecentLeadsCount,
    hasMoreLeads,
    showAllRecentLeads,
    setShowAllRecentLeads,

    recentFilter,
    setRecentFilter,
    recentSort,
    setRecentSort,

    showLeadsManagerFilters,
    showLeadsOwnerFilter,
    recentLocationFilter,
    setRecentLocationFilter,
    recentOwnerFilter,
    setRecentOwnerFilter,
    locationOptions,
    ownerCandidates,
    ownerLabelByUserId,
    locationNameById,

    openLeadInEditor,
    deleteLead,
    mergeLeadIntoCurrent,
    isMergeCandidate,

    formatLeadAge,

    currentLeadId,

    commitTranscriptToCurrentLead,

    // DEV TOOLS
    devMode,
    devToolsNode,
  } = props;

  const [recentLeadsOpen, setRecentLeadsOpen] = useLocalStorageState<boolean>("bb-recent-leads-open", true);

  // Toolbar buttons — sized for the new horizontal sticky strip (compare
  // with the retired full-width left-rail buttons that ran `py-8 text-6xl`
  // for `New Customer` and `py-3 text-xl` for Listen/Stop).
  const newCustomerClass =
    "shrink-0 px-5 py-2.5 text-base font-semibold rounded-xl shadow-sm transition-opacity text-white " +
    "bg-[var(--color-primary)] hover:opacity-90";
  const listenStopButtonClass =
    "shrink-0 px-5 py-2.5 text-base font-semibold rounded-xl shadow-sm transition-opacity text-white " +
    "bg-[var(--color-secondary)] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed";
  const closeLeadButtonClass =
    "shrink-0 px-4 py-2.5 text-base font-medium rounded-xl bg-gray-200 text-gray-800 " +
    "hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600";

  // Layout: dev-sync note 4de25b92 retired the desktop-only 2-column
  // (sticky 360px rail + workflow column) that landed in Slice 1 of
  // docs/planning/desktop_layout_pass.md §4.1.1. The Slice A/C/D
  // moves of `activity_dock_workflow_facilitation.md` §6 pulled
  // Location Links → sidebar, Daily Checklist → dock, and Unsent
  // Leads → dock, leaving the rail down to a button (or button +
  // controls). The unbalanced ~25/75 split looked floating; §6.1
  // resolved in favor of "retire the 2-column layout."
  //
  // Follow-up dev-sync note ec4dd70a polished the toolbar
  // implementation: the first pass landed a `bg-white/95
  // backdrop-blur` + `lg:border-b` sticky strip and rendered
  // `New Customer` + Listen/Stop + Close Lead side-by-side when a
  // lead was active. The visible white-box read as awkward
  // ("kinda in an awkward white box"), and putting `New Customer`
  // next to mid-workflow controls created a footgun (one click
  // away from torching the active lead). This pass removes the
  // box chrome entirely (no bg, no backdrop-blur, no border) and
  // makes the two control sets mutually exclusive: `New Customer`
  // when no lead, Listen/Stop + Close Lead when a lead is active.
  // Both states center themselves on the row. To start a new lead
  // mid-workflow the user closes the current one first — that's
  // the intentional flow.
  //
  // Sticky also dropped: a transparent sticky strip would
  // see-through scrolling content underneath, and adding the bg
  // back contradicts ec4dd70a's "no white box" ask. The toolbar
  // is now a regular in-flow row that scrolls with the page. The
  // controls are always near the top of the visible viewport on
  // first paint, which is what matters.
  return (
    <div className="space-y-6 text-left">
      {/* Stable capture-lead anchor: New Customer is unmounted while a lead is
          open, which made Ask/tour "Show me" highlights silently no-op. */}
      <div
        className="flex flex-wrap items-center justify-center gap-2 py-2"
        {...{ [FIREFLY_TARGET_ATTR]: "capture-lead" }}
      >
        {currentLead ? (
          <>
            {isListening ? (
              <button
                type="button"
                onClick={() => void stopListening()}
                disabled={isStoppingUi}
                className={listenStopButtonClass}
              >
                {isStoppingUi ? "Processing…" : "Stop Listening"}
              </button>
            ) : (
              <button
                type="button"
                {...{ [FIREFLY_TARGET_ATTR]: "capture-voice" }}
                onClick={continueListening}
                className={listenStopButtonClass}
              >
                Listen
              </button>
            )}

            <button type="button" onClick={closeCurrentLead} className={closeLeadButtonClass}>
              Close Lead
            </button>
          </>
        ) : (
          <button type="button" onClick={startNewLead} className={newCustomerClass}>
            New Customer
          </button>
        )}
      </div>

      <div className="space-y-8 text-left">
        {currentLead ? (
          <>
            <LeadsModeTranscriptBlock
              transcript={transcript}
              isListening={isListening}
              isAsrReconnecting={isAsrReconnecting}
              asrStatus={asrStatus}
              showTranscriptDetails={showTranscriptDetails}
              setShowTranscriptDetails={setShowTranscriptDetails}
              commitTranscriptToCurrentLead={commitTranscriptToCurrentLead}
            />

            {leadView}

            {leadSummaryNode}
          </>
        ) : null}

        {devMode && devToolsNode ? <div className="mt-10">{devToolsNode}</div> : null}

        <LeadsModeRecentLeadsBlock
          appDisplayName={appDisplayName}
          recentLeadsOpen={recentLeadsOpen}
          setRecentLeadsOpen={setRecentLeadsOpen}
          recentFilter={recentFilter}
          setRecentFilter={setRecentFilter}
          recentSort={recentSort}
          setRecentSort={setRecentSort}
          showLeadsManagerFilters={showLeadsManagerFilters}
          showLeadsOwnerFilter={showLeadsOwnerFilter}
          recentLocationFilter={recentLocationFilter}
          setRecentLocationFilter={setRecentLocationFilter}
          recentOwnerFilter={recentOwnerFilter}
          setRecentOwnerFilter={setRecentOwnerFilter}
          locationOptions={locationOptions}
          ownerCandidates={ownerCandidates}
          ownerLabelByUserId={ownerLabelByUserId}
          locationNameById={locationNameById}
          visibleRecentLeads={visibleRecentLeads}
          filteredRecentLeadsCount={filteredRecentLeadsCount}
          hasMoreLeads={hasMoreLeads}
          showAllRecentLeads={showAllRecentLeads}
          setShowAllRecentLeads={setShowAllRecentLeads}
          openLeadInEditor={openLeadInEditor}
          deleteLead={deleteLead}
          mergeLeadIntoCurrent={mergeLeadIntoCurrent}
          isMergeCandidate={isMergeCandidate}
          formatLeadAge={formatLeadAge}
          currentLeadId={currentLeadId}
          currentLead={currentLead}
        />
      </div>
    </div>
  );
}